import { useRef, useEffect } from 'react'
import { OrbitControls, useEnvironment, Environment, Text } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'

// Add this function at the top of your file
function isMobileDevice() {
  if (typeof navigator === 'undefined') return false;

  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Check for iOS devices
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;

  // Check for Android devices
  const isAndroid = /android/i.test(userAgent);

  return isIOS || isAndroid;
}

const CrystalBall = () => {
  const sphereRef = useRef()
  const baseGroupRef = useRef()  // New ref for all base components
  const entireModelRef = useRef() // New ref for the entire model (ball + stand)

  // Use environment loader instead of texture loader for HDRIs
  const envMap = useEnvironment({ files: '/textures/sf1.hdr' })

  const { gl, scene } = useThree()
  const { size } = useThree() // Get current viewport dimensions

  // Use our more sophisticated mobile detection
  const isMobile = isMobileDevice();
  const yOffset = isMobile ? -20 : -15 // Lower position on mobile

  // Create cube render target for reflections
  const cubeRenderTarget = new THREE.WebGLCubeRenderTarget(256, {
    type: THREE.HalfFloatType,
    generateMipmaps: true,
    minFilter: THREE.LinearMipmapLinearFilter,
    magFilter: THREE.LinearFilter
  })
  const cubeCamera = new THREE.CubeCamera(1, 1000, cubeRenderTarget)

  useEffect(() => {
    gl.toneMapping = THREE.ACESFilmicToneMapping
    gl.toneMappingExposure = 1.5
  }, [gl])

  useFrame((state) => {
    // Update reflections with base hidden
    if (sphereRef.current && baseGroupRef.current) {
      sphereRef.current.visible = false
      baseGroupRef.current.visible = false  // Hide base during reflection capture
      cubeCamera.update(state.gl, scene)
      sphereRef.current.visible = true
      baseGroupRef.current.visible = true   // Show base again
    }

    // Gentle floating animation for the entire model
    if (entireModelRef.current) {
      entireModelRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.5 + yOffset
    }
  })

  return (
    <>
      <Environment
        map={envMap}
        background
        blur={0.5}
        resolution={256}
      />

      {/* Group for the entire model (crystal ball + stand) */}
      <group ref={entireModelRef} position={[0, yOffset, 0]}>
        {/* Main crystal ball sphere */}
        <mesh ref={sphereRef} position={[0, 2, 0]}>
          <icosahedronGeometry args={[15, 8]} />
          <meshPhysicalMaterial
            envMap={cubeRenderTarget.texture}
            roughness={0.01}
            metalness={1}
            envMapIntensity={1}
            transparent={true}
            opacity={0.9}
          />
        </mesh>

        {/* Group all base components */}
        <group ref={baseGroupRef}>
          {/* Ornate base - main platform */}
          <mesh position={[0, -15, 0]}>
            <cylinderGeometry args={[18, 20, 3, 8]} />
            <meshStandardMaterial
              color="#4a1010"
              metalness={0.3}
              roughness={0.7}
            />
          </mesh>

          {/* Four decorative legs */}
          {[0, Math.PI / 2, Math.PI, Math.PI * 1.5].map((rotation, i) => (
            <group key={i} rotation={[0, rotation, 0]} position={[0, -14, 0]}>
              <mesh position={[16, 0, 0]}>
                <boxGeometry args={[4, 3, 4]} />
                <meshStandardMaterial
                  color="#2a0606"
                  metalness={0.4}
                  roughness={0.6}
                />
              </mesh>
              {/* Decorative curl on each leg */}
              <mesh position={[16, -1.5, 0]}>
                <torusGeometry args={[1, 0.5, 8, 8, Math.PI]} />
                <meshStandardMaterial
                  color="#3a0808"
                  metalness={0.5}
                  roughness={0.5}
                />
              </mesh>
            </group>
          ))}

          {/* Central pedestal */}
          <mesh position={[0, -6, 0]}>
            <cylinderGeometry args={[4, 8, 12, 8]} />
            <meshStandardMaterial
              color="#4a1010"
              metalness={0.3}
              roughness={0.7}
            />
          </mesh>

          {/* Simple top platform for the sphere */}
          <mesh position={[0, -1, 0]}>
            <cylinderGeometry args={[5, 4, 2, 8]} />
            <meshStandardMaterial
              color="#3a0808"
              metalness={0.4}
              roughness={0.6}
            />
          </mesh>
        </group>
      </group>

      <OrbitControls autoRotate autoRotateSpeed={1} />
      <ambientLight intensity={0.5} />
      <pointLight position={[50, 50, 50]} intensity={1.5} />
      <pointLight position={[-50, -50, -50]} intensity={1} />
      <spotLight position={[0, 50, 0]} intensity={1} angle={0.5} penumbra={1} />

      <fog attach="fog" args={['#000000', 50, 190]} />
    </>
  )
}

export default CrystalBall 